/**
 * 🔗 PAGE DE TRACKING AFFILIÉ
 * Route: /r/:affiliateCode
 * 
 * - Track le clic
 * - Stocke le cookie pendant 90 jours
 * - Redirige vers l'accueil Heartbeats
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Durée du cookie en jours
const COOKIE_DURATION_DAYS = 90;

// Fonction pour définir un cookie
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

// Fonction pour lire un cookie
const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export default function AffiliateRedirectPage() {
  const { affiliateCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('tracking');
  const [affiliateName, setAffiliateName] = useState('');

  useEffect(() => {
    const trackAndRedirect = async () => {
      if (!affiliateCode) {
        setStatus('error');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      try {
        // Appeler l'API de tracking
        const response = await fetch(`${API_URL}/api/affiliation/track/${affiliateCode.toUpperCase()}`);
        const data = await response.json();

        if (data.success) {
          // Stocker les infos dans les cookies (90 jours)
          setCookie('hb_ref', data.affiliateCode, COOKIE_DURATION_DAYS);
          setCookie('hb_click_id', data.clickId, COOKIE_DURATION_DAYS);
          
          // Stocker aussi dans localStorage pour backup
          localStorage.setItem('hb_affiliate_ref', JSON.stringify({
            code: data.affiliateCode,
            clickId: data.clickId,
            trackedAt: new Date().toISOString(),
            expiresAt: data.cookieExpiry
          }));

          setAffiliateName(data.affiliateName || '');
          setStatus('success');

          // Rediriger après un court délai
          setTimeout(() => {
            navigate('/auth?mode=signup&ref=' + data.affiliateCode);
          }, 2000);
        } else {
          setStatus('invalid');
          setTimeout(() => navigate('/'), 2000);
        }
      } catch (error) {
        console.error('Erreur tracking affilié:', error);
        setStatus('error');
        setTimeout(() => navigate('/'), 2000);
      }
    };

    trackAndRedirect();
  }, [affiliateCode, navigate]);

  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0a0015] flex items-center justify-center">
      {/* Background animé */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-pink-500/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 text-center p-8"
      >
        {/* Logo animé */}
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center"
        >
          <Heart className="w-12 h-12 text-white" fill="white" />
        </motion.div>

        {status === 'tracking' && (
          <>
            <h1 className="text-2xl font-bold text-white mb-2">
              Un instant...
            </h1>
            <p className="text-white/60">
              Préparation de ton accès privilégié
            </p>
            <motion.div
              className="mt-6 flex justify-center gap-2"
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-3 h-3 rounded-full bg-pink-500"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </motion.div>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="mb-4"
            >
              <Sparkles className="w-8 h-8 text-yellow-400 mx-auto" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Bienvenue sur Heartbeats ! 💜
            </h1>
            <p className="text-white/60">
              {affiliateName ? (
                <>Recommandé par <span className="text-pink-400 font-semibold">{affiliateName}</span></>
              ) : (
                'Tu vas adorer cette app de rencontres authentique'
              )}
            </p>
            <p className="text-white/40 text-sm mt-4">
              Redirection en cours...
            </p>
          </>
        )}

        {status === 'invalid' && (
          <>
            <h1 className="text-2xl font-bold text-white mb-2">
              Lien expiré ou invalide
            </h1>
            <p className="text-white/60">
              Redirection vers l'accueil...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-2xl font-bold text-white mb-2">
              Oups, petit problème
            </h1>
            <p className="text-white/60">
              Redirection vers l'accueil...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
